import React, { useEffect, useState, useMemo } from 'react';
import { collection, doc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import GalaUser from '../components/galaUser';

const FACILITY_ID = "SPdqELFz2ovWSn8eFyws";

export default function Declarations() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [ourAcc, setOurAcc] = useState([]);
  const [instructor, setInstructor] = useState(null);
  const [actualGroup, setActualGroup] = useState('');

  const [authReady, setAuthReady] = useState(false);
  const [personelReady, setPersonelReady] = useState(false);
  const [groupsReady, setGroupsReady] = useState(false);
  const [locationsReady, setLocationsReady] = useState(false);
  const [usersReady, setUsersReady] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => {
      setInstructor(u?.email || null);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authReady) return;

    if (!instructor) {
      setOurAcc([]);
      setPersonelReady(true);
      return;
    }

    setPersonelReady(false);

    const q = query(collection(db, 'personel'), where('mail', '==', instructor));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setOurAcc(snap.docs.map((d) => d.data()));
        setPersonelReady(true);
      },
      (error) => {
        console.error("Błąd ładowania personel:", error);
        setOurAcc([]);
        setPersonelReady(true);
      }
    );

    return () => unsub();
  }, [instructor, authReady]);

  useEffect(() => {
    if (!personelReady) return;

    if (!ourAcc?.[0]?.name) {
      setGroups([]);
      setGroupsReady(true);
      return;
    }

    setGroupsReady(false);

    const unsub = onSnapshot(
      doc(db, 'groups', FACILITY_ID),
      (snap) => {
        if (!snap.exists()) {
          setGroups([]);
          setGroupsReady(true);
          return;
        }

        const data = snap.data() || {};
        const allGroups = Object.entries(data)
          .map(([id, value]) => ({
            id,
            ...value,
          }))
          .filter((g) => g?.gala !== false);

        const filteredGroups = allGroups.filter((g) => {
          const instr = g?.instructor ?? g?.instructors;

          if (typeof instr === 'string') {
            return instr.includes(ourAcc[0].name);
          }

          if (Array.isArray(instr)) {
            return instr.some((i) => String(i || '').includes(ourAcc[0].name));
          }

          return false;
        });

        setGroups(filteredGroups);
        setGroupsReady(true);
      },
      (error) => {
        console.error("Błąd ładowania groups:", error);
        setGroups([]);
        setGroupsReady(true);
      }
    );

    return () => unsub();
  }, [ourAcc, personelReady]);

  useEffect(() => {
    const loadUsers = async () => {
      setUsersReady(false);

      try {
        const snap = await getDocs(collection(db, 'users2026'));
        const all = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        const list = [];

        all.forEach((acc) => {
          Object.values(acc?.subUsers || {}).forEach((sub) => {
            if (Array.isArray(sub?.groups)) {
              list.push({
                uid: acc?.uid || acc?.id || '',
                acc,
                user: sub,
              });
            }
          });
        });

        setUsers(list);
      } catch (error) {
        console.error("Błąd ładowania users2026:", error);
        setUsers([]);
      } finally {
        setUsersReady(true);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    setLocationsReady(false);

    const unsub = onSnapshot(
      collection(db, 'locations'),
      (snap) => {
        const all = snap.docs
          .map((d) => d.data())
          .reduce((a, o) => [...a, ...Object.values(o || {})], []);
        setLocations(all);
        setLocationsReady(true);
      },
      (error) => {
        console.error("Błąd ładowania locations:", error);
        setLocations([]);
        setLocationsReady(true);
      }
    );

    return () => unsub();
  }, []);

  function numberToPolishWeekday(n) {
    return {
      1: 'Poniedziałek',
      2: 'Wtorek',
      3: 'Środa',
      4: 'Czwartek',
      5: 'Piątek',
      6: 'Sobota',
      7: 'Niedziela',
    }[n] || '';
  }

  const getFirstDay = (group) => {
    if (Array.isArray(group?.days)) return group.days[0] || null;
    return group?.days || null;
  };

  const getGroupDeclarationStatus = (u, groupId) => {
    const value = u?.user?.gala?.[groupId];

    if (value === true) return 'yes';
    if (value === false) return 'no';
    return 'empty';
  };

  const userList = useMemo(() => {
    return (status) => {
      if (!actualGroup) return [];

      return users
        .filter((x) => Array.isArray(x?.user?.groups) && x.user.groups.includes(actualGroup))
        .filter((x) => getGroupDeclarationStatus(x, actualGroup) === status)
        .sort((a, b) =>
          String(a?.user?.displayName || '')
            .toUpperCase()
            .localeCompare(String(b?.user?.displayName || '').toUpperCase(), 'pl')
        );
    };
  }, [users, actualGroup]);

  const loading =
    !authReady ||
    !personelReady ||
    !groupsReady ||
    !locationsReady ||
    !usersReady;

  return (
    <div
      className="side-margin"
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        flexDirection: 'column',
        minHeight: '100vh',
        overflowY: 'scroll',
        gap: '15px',
        paddingTop: '20px',
        paddingBottom: '20px',
      }}
    >
      {loading && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            gap: "15px",
            position: "fixed",
            top: "0px",
            left: "0px",
            minHeight: "100vh",
            zIndex: "50",
            backgroundColor: "rgb(250,250,250)",
          }}
        >
          <div className="lds-default">
            <div></div><div></div><div></div><div></div>
            <div></div><div></div><div></div><div></div>
            <div></div><div></div><div></div><div></div>
          </div>
          <h3 style={{ fontSize: "18px", color: "rgb(20,20,20)" }}>
            Ładowanie danych...
          </h3>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          gap: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            position: 'relative',
            width: '100%',
            gap: '5px',
            maxWidth: '800px',
          }}
        >
          <div
            className="mobile-column"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              width: '100%',
              gap: '5px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                width: '400px',
                gap: '10px',
              }}
            >
              <Link to="/frekwencja" style={{ textDecoration: 'none' }}>
                <button
                  style={{
                    width: '70px',
                    height: '35px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: 'rgb(250,250,250)',
                    backgroundColor: '#0045C7',
                    borderRadius: '5px',
                    border: '0',
                    outline: 'none',
                  }}
                >
                  Powrót
                </button>
              </Link>

              <select
                value={actualGroup}
                onChange={(e) => setActualGroup(e.target.value)}
                style={{
                  height: '37.5px',
                  padding: '7px',
                  maxWidth: '320px',
                  outline: 'none',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(0,0,0,0)',
                  border: '1px solid rgba(30,30,30,0.25)',
                  color: 'rgb(100,100,100)',
                  fontSize: '12px',
                  borderRadius: '5px',
                }}
              >
                <option value="">Wybierz grupę</option>

                {[...groups]
                  .sort((a, b) => {
                    const nA = locations?.find((x) => x?.id === a?.location_id)?.name || '';
                    const nB = locations?.find((x) => x?.id === b?.location_id)?.name || '';
                    return nA.localeCompare(nB, 'pl');
                  })
                  .map((g, i) => {
                    const firstDay = getFirstDay(g);

                    return (
                      <option key={i} value={g?.id}>
                        {locations?.find((x) => x?.id === g?.location_id)?.name} -{' '}
                        {firstDay?.custom_name
                          ? firstDay.custom_name
                          : `${numberToPolishWeekday(firstDay?.weekDay)} - ${firstDay?.startTime || ''} - ${firstDay?.endTime || ''} - ${g?.type || ''}`}
                      </option>
                    );
                  })}
              </select>
            </div>
          </div>
        </div>

        <div style={{ width: '90%' }}>
          {!actualGroup && (
            <h2
              style={{
                fontSize: '15px',
                margin: '40px 0',
                color: 'rgba(30,30,30,0.6)',
                width: '100%',
                textAlign: 'center',
              }}
            >
              Nie wybrano grupy
            </h2>
          )}

          {actualGroup &&
            userList('yes').length === 0 &&
            userList('no').length === 0 &&
            userList('empty').length === 0 && (
              <h2
                style={{
                  fontSize: '15px',
                  margin: '40px 0',
                  color: 'rgba(30,30,30,0.45)',
                  width: '100%',
                  textAlign: 'center',
                  fontWeight: 'normal',
                }}
              >
                Brak uczestników
              </h2>
            )}

          {userList('yes').length > 0 && (
            <>
              <h2 style={{ fontSize: '15px', margin: '5px 0', color: '#0045C7' }}>
                Wystąpi na gali
              </h2>
              {userList('yes').map((u) => (
                <GalaUser
                  key={`${u.uid}-${u.user.index}-${u.user.displayName}`}
                  u={u}
                  locations={locations}
                  groups={groups}
                />
              ))}
            </>
          )}

          {userList('no').length > 0 && (
            <>
              <h2 style={{ fontSize: '15px', margin: '20px 0 5px', color: '#DC143C' }}>
                Nie wystąpi na gali
              </h2>
              {userList('no').map((u) => (
                <GalaUser
                  key={`${u.uid}-${u.user.index}-${u.user.displayName}`}
                  u={u}
                  locations={locations}
                  groups={groups}
                />
              ))}
            </>
          )}

          {userList('empty').length > 0 && (
            <>
              <h2 style={{ fontSize: '15px', margin: '20px 0 5px', color: 'rgb(30,30,30,0.7)' }}>
                Brak deklaracji
              </h2>
              {userList('empty').map((u) => (
                <GalaUser
                  key={`${u.uid}-${u.user.index}-${u.user.displayName}`}
                  u={u}
                  locations={locations}
                  groups={groups}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}